import {
    LockerPreparation,
    LockerCapacityError,
    createLockerInspector,
    createLockerRestorer,
    createLockerReserveRuntime,
    type LockerPreparationDependencies,
    type LockerPrepareRequest,
    type LockerReady,
    type LockerRentPriceFailure,
    type LockerRentPricePolicy,
    type LockerResolution,
    type LockerReserveOptions,
    type LockerReserveRuntime,
    type LockerSigned,
    type LockerValidatedReserveProfile,
} from '@aquariusdefi/sdk-phase1';
import { Transaction, xdr } from '@stellar/stellar-sdk';
import { rpc as lockerRpc } from '@stellar/stellar-sdk-15';

import { BASE_FEE } from 'constants/stellar';

import { getNetworkPassphrase } from 'helpers/env';
import PromisedTimeout from 'helpers/promised-timeout';
import { getSorobanUrl } from 'helpers/url';

import { createNetworkLockerPreparationFactory } from './lockerPreparation.network';

// The factory owns SDK-version-specific RPC, inspector, restorer and measured budget resolver.
// Only strings cross into the frontend's Stellar SDK and wallet implementations.
type LockerPreparationSession = Pick<
    LockerPreparation,
    'prepare' | 'sign' | 'submit' | 'reconcile'
>;

export interface LockerPreparationFactory {
    create: (context: {
        networkPassphrase: string;
        rpcUrl: string;
        source: string;
        purpose: 'preview' | 'submit';
        signRestore?: (envelopeXdr: string) => Promise<string>;
    }) => LockerPreparationSession;
    // Read-only: no simulation, restoration, authorization or wallet prompts.
    assertAvailable: (request: LockerPrepareRequest) => Promise<void>;
}

// Server construction stays on SDK15: passing the application's SDK16 rpc.Server is unsupported.
export const createLockerPreparationFactory = (options: {
    createServer: (rpcUrl: string) => Parameters<typeof createLockerInspector>[0];
    fee: string;
    profiles: readonly LockerValidatedReserveProfile[];
    createReserveRuntime?: (
        server: Parameters<typeof createLockerInspector>[0],
        options: LockerReserveOptions,
    ) => LockerReserveRuntime;
    rentPricePolicy?: LockerRentPricePolicy;
    signRestore?: (envelopeXdr: string, source: string) => Promise<string>;
    addressAuth?: LockerPreparationDependencies['addressAuth'];
}): LockerPreparationFactory => {
    const createRuntime = (
        server: Parameters<typeof createLockerInspector>[0],
        networkPassphrase: string,
    ) => {
        const runtimeOptions = {
            networkPassphrase,
            ...(options.rentPricePolicy ? { rentPricePolicy: options.rentPricePolicy } : {}),
        };
        return options.createReserveRuntime
            ? options.createReserveRuntime(server, runtimeOptions)
            : createLockerReserveRuntime(server, options.profiles, runtimeOptions);
    };
    return {
        assertAvailable: request =>
            Promise.resolve().then(() => {
                const server = options.createServer(getSorobanUrl());
                createRuntime(server, getNetworkPassphrase()).assertAvailable(request);
            }),
        create: context => {
            const server = options.createServer(context.rpcUrl);
            const runtime = createRuntime(server, context.networkPassphrase);
            return new LockerPreparation(server, context.networkPassphrase, options.fee, {
                prerequisites: request =>
                    Promise.resolve().then(() => runtime.assertAvailable(request)),
                inspect: createLockerInspector(server),
                restore:
                    context.purpose === 'preview'
                        ? () =>
                              Promise.reject(
                                  new Error(
                                      'Locker state needs restoration. Prepare the withdrawal before previewing it again.',
                                  ),
                              )
                        : createLockerRestorer(server, {
                              networkPassphrase: context.networkPassphrase,
                              fee: options.fee,
                              sign: envelope => {
                                  if (context.signRestore) return context.signRestore(envelope);
                                  if (options.signRestore)
                                      return options.signRestore(envelope, context.source);
                                  return Promise.reject(
                                      new Error(
                                          'Connect the Locker wallet before restoring state.',
                                      ),
                                  );
                              },
                          }),
                budget: runtime.budget,
                addressAuth: context.purpose === 'preview' ? undefined : options.addressAuth,
            });
        },
    };
};

// Build from the current RPC simulation; network configuration changes do not disable Locker.
export const createDefaultLockerPreparationFactory = (): LockerPreparationFactory =>
    createNetworkLockerPreparationFactory({
        createServer: rpcUrl => new lockerRpc.Server(rpcUrl),
        fee: BASE_FEE,
    });

export interface LockerTransactionSigner {
    signTx: (transaction: Transaction) => Promise<string>;
}

export class LockerTransactionError extends Error {
    readonly status: LockerResolution['status'];
    readonly hash: string;
    readonly envelopeXdr?: string;
    readonly evidence?: LockerRentPriceFailure;

    constructor(result: LockerResolution, message?: string) {
        const messages = {
            unknown: `Locker transaction ${result.hash} is still awaiting confirmation. Retry this same operation to check its status before starting another transaction.`,
            'rent-price-retry': `Locker transaction ${result.hash} failed because the storage price changed. Review the new fee, then prepare and sign again.`,
            'rebuild-required': `Locker transaction ${result.hash} was rejected because the account sequence changed. Prepare and sign again.`,
            failed: `Locker transaction ${result.hash} failed. Prepare and sign again.`,
            success: `Locker transaction ${result.hash} succeeded. Review your locks before starting another operation.`,
        };
        super(message ?? messages[result.status]);
        this.name = 'LockerTransactionError';
        this.status = result.status;
        this.hash = result.hash;
        if (result.status !== 'success') this.envelopeXdr = result.envelopeXdr;
        if (result.status === 'rent-price-retry') this.evidence = result.evidence;
    }
}

export interface PreparedLockerTransaction {
    readonly ready: LockerReady;
}

type Session = {
    preparation: LockerPreparationSession;
    requestKey?: string;
    handle?: PreparedLockerTransaction;
    signed?: LockerSigned;
    busy: boolean;
    networkPassphrase: string;
    signer?: LockerTransactionSigner;
};

// Match the ordinary Soroban confirmation window without resending the transaction.
const CONFIRMATION_ATTEMPTS = 30;
const CONFIRMATION_RETRY_MS = 2000;

export class LockerTransactions {
    private factory?: LockerPreparationFactory;
    private sessions = new Map<string, Session>();
    private handles = new WeakMap<PreparedLockerTransaction, Session>();

    configure(factory: LockerPreparationFactory) {
        if ([...this.sessions.values()].some(session => session.busy || session.signed)) {
            throw new Error(
                'Resolve the pending Locker transaction before changing configuration.',
            );
        }
        this.factory = factory;
        this.sessions.clear();
        this.handles = new WeakMap();
    }

    assertConfigured() {
        if (!this.factory) {
            throw new Error(
                'Locker transactions are temporarily unavailable. Please try again after the Locker update.',
            );
        }
    }

    private context(source: string) {
        return {
            networkPassphrase: getNetworkPassphrase(),
            rpcUrl: getSorobanUrl(),
            source,
        };
    }

    private session(source: string) {
        this.assertConfigured();
        const context = this.context(source);
        const key = JSON.stringify(context);
        if (!this.sessions.has(key)) {
            this.sessions.set(key, {
                preparation: this.factory.create({
                    ...context,
                    purpose: 'submit',
                    signRestore: envelope => {
                        const signer = this.sessions.get(key)?.signer;
                        if (!signer)
                            return Promise.reject(
                                new Error('Connect the Locker wallet before restoring state.'),
                            );
                        return signer.signTx(new Transaction(envelope, context.networkPassphrase));
                    },
                }),
                busy: false,
                networkPassphrase: context.networkPassphrase,
            });
        }
        return this.sessions.get(key);
    }

    // Run before Classic trustlines or any other paid/wallet prerequisite. Final SDK
    // preparation still repeats availability and obtains fresh state after prerequisites.
    async preflight(request: LockerPrepareRequest): Promise<PreparedLockerTransaction | undefined> {
        this.assertConfigured();
        const existing = this.sessions.get(JSON.stringify(this.context(request.source)));
        if (existing?.signed) {
            // Resolving an already-sent hash needs no profile for a new transaction.
            await this.reconcileChangedRequest(existing, JSON.stringify(request));
            return existing.handle;
        }
        try {
            await this.factory.assertAvailable(request);
        } catch (error) {
            if (error instanceof LockerCapacityError) throw error;
            throw new Error(
                'Locker transactions are temporarily unavailable. Please try again later.',
            );
        }
        await this.reconcileChangedRequest(this.session(request.source), JSON.stringify(request));
    }

    private clear(session: Session) {
        if (session.handle) this.handles.delete(session.handle);
        session.signed = undefined;
        session.handle = undefined;
        session.requestKey = undefined;
        session.signer = undefined;
    }

    private async reconcileChangedRequest(session: Session, requestKey: string) {
        if (session.busy) throw new Error('A Locker transaction is already in progress.');
        if (!session.signed || session.requestKey === requestKey) return;
        session.busy = true;
        try {
            const result = await session.preparation.reconcile(session.signed);
            if (result.status === 'unknown') {
                throw new LockerTransactionError(result);
            }
            this.clear(session);
            throw new LockerTransactionError(
                result,
                `Your previous Locker transaction ${result.hash} ${result.status === 'success' ? 'succeeded' : 'failed'}. Review your locks and retry the selected operation.`,
            );
        } finally {
            session.busy = false;
        }
    }

    async prepare(
        request: LockerPrepareRequest,
        signer?: LockerTransactionSigner,
    ): Promise<PreparedLockerTransaction> {
        await this.preflight(request);
        const session = this.session(request.source);
        const requestKey = JSON.stringify(request);
        if (session.busy) throw new Error('A Locker transaction is already in progress.');
        if (session.signed) {
            if (session.requestKey === requestKey) return session.handle;
            throw new Error(
                `Check the pending Locker transaction ${session.signed.hash} before starting another operation.`,
            );
        }
        session.busy = true;
        try {
            session.signer = signer;
            const ready = await session.preparation.prepare(request);
            if (session.handle) this.handles.delete(session.handle);
            const handle = Object.freeze({ ready });
            session.handle = handle;
            session.requestKey = requestKey;
            this.handles.set(handle, session);
            return handle;
        } finally {
            session.busy = false;
        }
    }

    // A separate read-only instance preserves live handles and never restores/signs auth.
    async preview(request: LockerPrepareRequest): Promise<void> {
        this.assertConfigured();
        try {
            await this.factory.assertAvailable(request);
        } catch (error) {
            if (error instanceof LockerCapacityError) throw error;
            throw new Error(
                'Locker transactions are temporarily unavailable. Please try again later.',
            );
        }
        const preparation = this.factory.create({
            networkPassphrase: getNetworkPassphrase(),
            rpcUrl: getSorobanUrl(),
            source: request.source,
            purpose: 'preview',
        });
        await preparation.prepare(request);
    }

    async signAndSubmit(
        handle: PreparedLockerTransaction,
        signer: LockerTransactionSigner,
    ): Promise<xdr.ScVal | undefined> {
        const session = this.handles.get(handle);
        if (!session || session.handle !== handle)
            throw new Error('Prepare the Locker transaction again.');
        if (session.busy) throw new Error('A Locker transaction is already in progress.');
        if (session.networkPassphrase !== getNetworkPassphrase())
            throw new Error(
                'Return to the original Locker network before continuing this transaction.',
            );
        session.busy = true;
        try {
            let result: LockerResolution;
            if (session.signed) {
                // Repeated UI attempts only reconcile the original hash; never prompt or resend.
                result = await session.preparation.reconcile(session.signed);
            } else {
                session.signed = await session.preparation.sign(handle.ready, envelope =>
                    signer.signTx(new Transaction(envelope, session.networkPassphrase)),
                );
                result = await session.preparation.submit(session.signed);
            }
            // RPC may not see a just-submitted transaction until a later ledger.
            // Keep the original signed handle busy while checking its exact hash.
            for (
                let attempt = 1;
                result.status === 'unknown' && attempt < CONFIRMATION_ATTEMPTS;
                attempt += 1
            ) {
                await PromisedTimeout(CONFIRMATION_RETRY_MS);
                result = await session.preparation.reconcile(session.signed);
            }
            if (result.status === 'unknown') {
                throw new LockerTransactionError(result);
            }
            this.clear(session);
            if (result.status !== 'success') {
                throw new LockerTransactionError(result);
            }
            // Receipts come only from the included transaction, never its simulation.
            return result.returnValueXdr
                ? xdr.ScVal.fromXdr(result.returnValueXdr, 'base64')
                : undefined;
        } finally {
            session.busy = false;
        }
    }
}
